import type { Root } from 'mdast';
import { defaultHandlers, type Handlers } from 'mdast-util-to-markdown';
import remarkMath from 'remark-math';
import remarkStringify from 'remark-stringify';
import { unified } from 'unified';

import type { Document } from '@gitbook/slate';
import { Trace } from '@gitbook/util-debug';

import { remarkGfm } from '../../utils';
import { createFormattingContext } from '../context';
import { defaultFormattingOptions, type FormattingOptions } from '../options';
import { handleCustomBlockToMarkdown } from './compiler-custom-blocks';
import { compileLiquidTags } from './compiler-liquid-tags';
import { gastToMdast } from './gastToMdast';

const handlers: Handlers = {
    // @ts-expect-error - it's a custom node type
    'gb-custom-block': handleCustomBlockToMarkdown,
    code: compileLiquidTags(defaultHandlers.code),
};

/**
 * Format a document to a markdown string.
 */
export async function formatMarkdown(
    document: Document,
    partialOptions: Partial<FormattingOptions> = {}
): Promise<string> {
    const options = {
        ...defaultFormattingOptions,
        ...partialOptions,
    };
    const context = createFormattingContext(options);
    const trace = new Trace('format.formatMarkdown', { silent: true, sampleRate: 0 });

    return trace.wrap(async () => {
        const tree = await gastToMdast(document, context);

        const pipeline = unified()
            .use(remarkStringify, {
                handlers,
                bullet: '*',
                strong: '*',
                emphasis: '_',
                fences: true,
                listItemIndent: 'mixed',
                resourceLink: true,
                unsafe: [
                    // Escape all non custom liquid tags from the content using these rules
                    // character is the character we're looking for, it will add a forward slash right before it.
                    // inConstruct tells mdast to only check in these type of blocks
                    // atBreak tells mdast to only check after a line break (So first char)
                    // before tells mdast to only escape if the character match AND this is before the character
                    { character: '{%', inConstruct: ['phrasing'] },
                    { atBreak: true, character: '{%' },
                    { before: '%', character: '}', inConstruct: ['phrasing'] },
                    { atBreak: true, before: '%', character: '}' },
                    { character: '{{', inConstruct: ['phrasing'] },
                    { atBreak: true, character: '{{' },
                    { character: '}}', inConstruct: ['phrasing'] },
                    { atBreak: true, character: '}}' },
                ],
            })
            .use(remarkGfm)
            .use(remarkMath, {
                singleDollarTextMath: false,
            });

        const processedTree = (await pipeline.run(tree)) as Root;
        return pipeline.stringify(processedTree).toString();
    });
}
